import PropTypes from 'prop-types';
import React from 'react';
import { ReactSortable } from 'react-sortablejs';
import styled, { useTheme } from 'styled-components';
import { Icon, inkA, orange, PlainButton, tokens } from 'components/v3';
import { useBuilder } from '../BuilderContext';
import { CANVAS_ID } from '../hooks/useFieldFocus';
import { ErrorNote } from '../properties/parts';
import { duplicateFieldAction } from '../state/builderReducer';
import {
  CANVAS_GROUP,
  canvasItems,
  DRAG_HANDLE_CLASS,
  DROP_GHOST_CLASS,
  isSortableNoop,
  prepareSortableItems,
  setDragData,
} from '../state/sortableBridge';
import FieldCard from './FieldCard';
import SheetHeader from './SheetHeader';

// The form sheet in the middle of the builder (design: .canvas-sheet).
// Desktop: fields reorder by their grip and accept palette drops
// (react-sortablejs, plan §5.9). Phones: no drag; each card has Edit and ⋯.
// Archived forms render read-only. Locked fields (BuilderContext
// `isLocked`, w9-plan §6.5) render read-only cards with a lock; the
// builder's `lockedFieldIds` default leaves every card as before.

const AddFieldButton = styled(PlainButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 48px;
  margin-top: 4px;
  border-radius: 10px;
  border: 1.5px dashed ${(p) => p.$border};
  color: ${(p) => p.$color};
  font-size: 14px;
  font-weight: 600;
  transition: border-color 150ms ease, background 150ms ease;

  &:hover,
  &:active {
    border-color: ${orange(1)};
    background: ${orange(0.06)};
  }
`;

// While dragging, the placeholder (a card being moved, or a palette item
// on its way in) collapses into the design's drop line: 3px of orange with
// a soft glow, contents hidden.
const SortableArea = styled.div`
  position: relative;

  && .${DROP_GHOST_CLASS} {
    height: 3px;
    min-height: 0;
    margin: 2px 0;
    padding: 0;
    border: 0;
    border-radius: 3px;
    overflow: hidden;
    opacity: 1;
    background: ${orange(1)};
    box-shadow: 0 0 0 2px ${orange(0.25)}, 0 0 20px ${orange(0.2)};
    transform: none;
  }

  && .${DROP_GHOST_CLASS} > * {
    display: none;
  }
`;

const EmptyState = ({ overlay }) => {
  const theme = useTheme();
  const t = tokens(theme);
  return (
    <div
      style={{
        ...(overlay
          ? { position: 'absolute', inset: 0, pointerEvents: 'none' }
          : { minHeight: 180 }),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        padding: '40px 24px',
        boxSizing: 'border-box',
        textAlign: 'center',
        color: t.faint,
        border: '2px dashed ' + inkA(theme, 0.18),
        borderRadius: 14,
      }}
    >
      <Icon name="layout" size={34} color="currentColor" />
      <div style={{ fontSize: 15, fontWeight: 600, color: t.sub }}>
        Add fields to build your form
      </div>
      <div style={{ fontSize: 13 }}>
        Tap a field in the palette, or drag it onto the sheet
      </div>
    </div>
  );
};

EmptyState.propTypes = { overlay: PropTypes.bool };
EmptyState.defaultProps = { overlay: false };

const Canvas = ({
  variant,
  onAddField,
  onEditField,
  onMoreField,
  onSelectField,
}) => {
  const theme = useTheme();
  const t = tokens(theme);
  const {
    state,
    dispatch,
    readOnly,
    assetUrls,
    uploadAsset,
    uploading,
    showToast,
    isLocked,
  } = useBuilder();
  const { fields } = state.working;
  const locked = (id) => !!isLocked && isLocked(id);
  const mobile = variant === 'mobile';
  const sortable = !mobile && !readOnly;

  const assetUrl = (id) => (id != null ? assetUrls[id] || null : null);

  const pickAsset = (field) => async (key, file) => {
    const assetId = await uploadAsset(file);
    if (assetId)
      dispatch({
        type: 'UPDATE_FIELD',
        id: field.id,
        patch: { config: { [key]: assetId } },
      });
  };

  const cards = fields.map((field) => (
    <FieldCard
      key={field.id}
      field={field}
      variant={mobile ? 'mobile' : 'desktop'}
      selected={state.selectedId === field.id}
      error={state.errors[`definition.${field.id}`] || null}
      readOnly={readOnly}
      locked={locked(field.id)}
      assetUrl={assetUrl}
      uploading={uploading}
      onSelect={() => onSelectField(field.id)}
      onDuplicate={() => dispatch(duplicateFieldAction(field.id))}
      onDelete={() => {
        dispatch({ type: 'REMOVE_FIELD', id: field.id });
        showToast('Field deleted');
      }}
      onEdit={() => onEditField(field.id)}
      onMore={() => onMoreField(field.id)}
      onPickAsset={pickAsset(field)}
    />
  ));

  const listStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minHeight: fields.length ? 0 : 180,
  };

  const list = sortable ? (
    <SortableArea>
      <ReactSortable
        list={canvasItems(fields)}
        setList={(items) => {
          // Mount / choose / unchoose call this with the same order.
          if (isSortableNoop(fields, items)) return;
          dispatch({
            type: 'APPLY_SORTABLE',
            items: prepareSortableItems(items),
          });
        }}
        group={CANVAS_GROUP}
        handle={`.${DRAG_HANDLE_CLASS}`}
        ghostClass={DROP_GHOST_CLASS}
        animation={180}
        setData={setDragData}
        style={listStyle}
      >
        {cards}
      </ReactSortable>
      {fields.length ? null : <EmptyState overlay />}
    </SortableArea>
  ) : (
    <div style={listStyle}>{fields.length ? cards : <EmptyState />}</div>
  );

  return (
    <section
      id={CANVAS_ID}
      data-tour="canvas"
      tabIndex={-1}
      aria-label="Form fields"
      style={{
        outline: 'none',
        maxWidth: 720,
        margin: '0 auto',
        background: t.card,
        border: '1px solid ' + t.border,
        borderRadius: 16,
        boxShadow: t.shadowCard,
        overflow: 'hidden',
      }}
    >
      <SheetHeader mobile={mobile} />
      <div style={{ padding: mobile ? '10px 10px 18px' : '16px 24px 28px' }}>
        <ErrorNote>{state.errors.definition}</ErrorNote>
        {list}
        {fields.length && !readOnly ? (
          <AddFieldButton
            aria-label="Add field at the end"
            onClick={onAddField}
            $border={inkA(theme, 0.2)}
            $color={t.sub}
          >
            <Icon name="plus" size={16} color={orange(1)} />
            Add field
          </AddFieldButton>
        ) : null}
      </div>
    </section>
  );
};

Canvas.propTypes = {
  variant: PropTypes.oneOf(['desktop', 'mobile']),
  onAddField: PropTypes.func.isRequired,
  onSelectField: PropTypes.func.isRequired,
  onEditField: PropTypes.func,
  onMoreField: PropTypes.func,
};

Canvas.defaultProps = {
  variant: 'desktop',
  onEditField: () => undefined,
  onMoreField: () => undefined,
};

export default Canvas;
